import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  getUitnodigingTypeLabel,
  ProfielId,
  Rol,
  rollen,
  UitnodigingType,
  validatedProfielId,
} from 'parkour-web-app-dto';
import { getRolLabel } from '../../../../profiel/rol-labels';
import { UitnodigingenService } from '../../../../uitnodiging/service/uitnodinginen.service';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
  ViewWillEnter,
} from '@ionic/angular/standalone';
import { UitnodigingUpsert } from '../../../../uitnodiging/model/uitnodiging';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ParkourButtonComponent,
  ParkourFormFieldComponent,
  ParkourFormFieldWithoutLabelComponent,
  ParkourIconComponent,
  ParkourInputComponent,
  ParkourPageImageComponent,
  ParkourRadioComponent,
  ParkourSelectComponent,
  ParkourToastService,
} from '@parkour/ui';
import { DirtyFormCheck } from '../../../../shared/components/dirty-form-guard';
import { TeamService } from '../../../service/team.service';
import { finalize, forkJoin, take } from 'rxjs';
import { ParkourBackButtonDirective } from '../../../../shared/directives/parkour-back-button.directive';
import { TranslateModule } from '@ngx-translate/core';
import { ParkourHeaderActionComponent } from '../../../../shared/components/parkour-header-action/parkour-header-action.component';
import { PageLayoutComponent } from '../../../../shared/components/page-layout/page-layout.component';
import AuthService from '../../../../authentication/service/auth.service';
import { HumanReadableError } from '../../../../core/human-readable-error';

const rollenWithDefault = [...rollen, 'DEFAULT'];
type RolWithDefault = (typeof rollenWithDefault)[number];
type RegisterTeamlidFormGroup = FormGroup<{
  volledigeNaam: FormControl<string>;
  rol: FormControl<RolWithDefault>;
  email: FormControl<string | undefined>;
  sms: FormControl<string | undefined>;
  type: FormControl<UitnodigingType>;
  profielId: FormControl<ProfielId | undefined>;
}>;

@Component({
  templateUrl: './add-teamlid.page.html',
  standalone: true,
  imports: [
    ParkourBackButtonDirective,
    TranslateModule,
    ParkourPageImageComponent,
    ParkourFormFieldComponent,
    ParkourRadioComponent,
    ParkourFormFieldWithoutLabelComponent,
    ParkourIconComponent,
    ParkourButtonComponent,
    ParkourInputComponent,
    ParkourSelectComponent,
    ParkourHeaderActionComponent,
    PageLayoutComponent,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    IonBackButton,
    IonContent,
    IonRadioGroup,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class AddTeamlidPage implements ViewWillEnter, DirtyFormCheck {
  registerTeamlidForm!: RegisterTeamlidFormGroup;

  busy = false;

  rolOptions = [
    {
      label: '',
      value: 'DEFAULT',
    },
  ].concat(
    Object.entries(rollen).map(([, value]) => ({
      value: value,
      label: getRolLabel(value),
    })),
  );

  protected readonly getUitnodigingTypeLabel = getUitnodigingTypeLabel;

  constructor(
    private readonly uitnodigingenService: UitnodigingenService,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastService: ParkourToastService,
    private readonly teamService: TeamService,
    private readonly authService: AuthService,
  ) {
    this.registerTeamlidForm = this.fb.nonNullable.group(
      {
        volledigeNaam: this.fb.nonNullable.control<string>('', Validators.required),
        rol: this.fb.nonNullable.control<RolWithDefault>('DEFAULT', Validators.required),
        type: this.fb.nonNullable.control<UitnodigingType>('EMAIL', Validators.required),
        email: this.fb.nonNullable.control<string | undefined>(undefined),
        sms: this.fb.nonNullable.control<string | undefined>(undefined),
        profielId: this.fb.nonNullable.control<ProfielId | undefined>(undefined),
      },
      {
        validators: [uitnodigingTypeValidator, rolValidator],
        updateOn: 'change',
      },
    );
    this.registerTeamlidForm.controls.type.valueChanges.subscribe((selectedValue) => {
      if (selectedValue === 'EMAIL' || selectedValue === 'SMS') {
        this.registerTeamlidForm.patchValue({ profielId: undefined });
      } else if (selectedValue === 'QR') {
        this.registerTeamlidForm.patchValue({ email: undefined });
        this.registerTeamlidForm.patchValue({ sms: undefined });
      }
      if (selectedValue === 'SMS') {
        this.registerTeamlidForm.patchValue({ sms: '+32' });
      }
      if (selectedValue === 'EMAIL') {
        this.registerTeamlidForm.patchValue({ sms: undefined });
      }
    });

    this.route.queryParamMap.subscribe((params) => {
      if (params.has('profielId')) {
        const profielId = validatedProfielId(params.get('profielId'));

        forkJoin([
          this.teamService.getMyTeamleden().pipe(take(1)),
          this.authService.getAangemeldeUser$(),
        ]).subscribe(([teamleden, user]) => {
          if (teamleden.some((teamlid) => teamlid.id === profielId)) {
            throw new HumanReadableError('Deze persoon is al een teamlid.');
          }
          if (user.profielId === profielId) {
            throw new Error('Je kunt jezelf niet uitnodigen');
          }

          this.registerTeamlidForm.patchValue({
            type: 'QR',
            profielId: validatedProfielId(params.get('profielId')),
          });
        });
      }
    });
  }

  ionViewWillEnter(): void {
    this.busy = false;
  }

  onSubmit() {
    this.registerTeamlidForm.markAllAsTouched();

    if (this.registerTeamlidForm.valid) {
      this.busy = true;
      this.uitnodigingenService
        .sendUitnodiging(this.createUpsert())
        .pipe(finalize(() => (this.busy = false)))
        .subscribe(() => {
          this.registerTeamlidForm.reset();
          this.toastService.showToast({
            header: 'Uitnodiging verstuurd',
            content: 'De uitnodiging werd succesvol verstuurd.',
          });
          this.router.navigate(['..'], {
            relativeTo: this.route,
            replaceUrl: true,
          });
        });
    }
  }

  isFormDirty() {
    return this.registerTeamlidForm.dirty;
  }

  canOverrideDirtyForm(nextStateUrl: string) {
    return nextStateUrl.endsWith('qr-code-scan');
  }

  getEmailErrors() {
    return {
      ...this.registerTeamlidForm.errors,
      ...this.registerTeamlidForm.controls.email.errors,
    };
  }

  getRolErrors() {
    return {
      ...this.registerTeamlidForm.errors,
      ...this.registerTeamlidForm.controls.rol.errors,
    };
  }

  getSmsErrors() {
    return {
      ...this.registerTeamlidForm.errors,
      ...this.registerTeamlidForm.controls.sms.errors,
    };
  }

  getProfielIdErrors() {
    return {
      ...this.registerTeamlidForm.errors,
      ...this.registerTeamlidForm.controls.profielId.errors,
    };
  }

  private createUpsert(): UitnodigingUpsert {
    const rol: Rol = <Rol>this.registerTeamlidForm.getRawValue().rol;
    const type = this.registerTeamlidForm.getRawValue().type;
    const baseUpsert = {
      naam: this.registerTeamlidForm.getRawValue().volledigeNaam,
      rol: rol,
    };

    switch (type) {
      case 'EMAIL': {
        const email = this.registerTeamlidForm.getRawValue().email;
        if (!email) {
          throw new Error();
        }
        return {
          ...baseUpsert,
          email,
          type,
        };
      }
      case 'SMS': {
        const mobielNummer = this.registerTeamlidForm.getRawValue().sms;
        if (!mobielNummer) {
          throw new Error();
        }
        return {
          ...baseUpsert,
          mobielNummer,
          type,
        };
      }
      case 'QR': {
        const profielId = this.registerTeamlidForm.getRawValue().profielId;
        if (!profielId) {
          throw new Error();
        }
        return {
          ...baseUpsert,
          profielId,
          type,
        };
      }
    }
  }
}

const uitnodigingTypeValidator: ValidatorFn = (form: AbstractControl): ValidationErrors | null => {
  switch (form.getRawValue().type) {
    case 'EMAIL': {
      return form.getRawValue().email ? null : { emailRequired: true };
    }
    case 'SMS': {
      return form.getRawValue().sms ? null : { mobielNummerRequired: true };
    }
    case 'QR': {
      return form.getRawValue().profielId ? null : { profielIdRequired: true };
    }
  }
  return null;
};

const rolValidator: ValidatorFn = (form: AbstractControl): ValidationErrors | null => {
  switch (form.getRawValue().rol) {
    case 'DEFAULT': {
      return { rolRequired: true };
    }
  }
  return null;
};
